import React, { useState } from 'react';
import '../App.css';
import { baseUrl } from '../components/BaseUrl';
import { Link } from 'react-router-dom';
import { Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import logo from '../assets/img/codeflare-app.webp';

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loginError, setLoginError] = useState(""); // New state for login error

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (loginError) {
      setLoginError("");
    }
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(prev => !prev);
  };

  const validateForm = () => {
    const { email, password } = formData;

    if (!email.trim()) {
      showError("Empty Submission", "Please enter your email");
      return false;
    }

    if (!password) {
      showError("Empty Submission", "Please enter your password");
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showError("Invalid Email", "Please enter a valid email address");
      return false;
    }

    return true;
  };

  const showError = (title, text) => {
    Swal.fire({
      title,
      text,
      icon: "error",
      confirmButtonText: "OK",
      backdrop: `rgba(143, 70, 0, 0.3)`
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setDisabled(true);
    setLoginError(""); // Clear previous errors

    try {
      const response = await fetch(`${baseUrl}login-user.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      // Check if response is OK
      if (!response.ok) {
        throw new Error(`${response.message}`);
      }

      const responseText = await response.text();
      // console.log('Raw response:', responseText);

      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        throw new Error('Invalid response from server');
      }

      console.log('Parsed response:', responseData);

      if (!responseData.status) {
        // Check if the error is due to user not found or invalid credentials
        if (responseData.message && (
            responseData.message.toLowerCase().includes('invalid') ||
            responseData.message.toLowerCase().includes('not found') ||
            responseData.message.toLowerCase().includes('incorrect') ||
            responseData.message.toLowerCase().includes('no user') ||
            responseData.message.toLowerCase().includes('Please verify your email before')
        )) {
          setLoginError("Invalid email or password");
        } else {
          throw new Error(responseData.message || 'Login failed');
        }
        return;
      }

      // Login successful
      handleSuccessfulLogin(responseData);

    } catch (error) {
      console.error('Login error:', error);
      // Only show SweetAlert for non-credential errors
      if (!loginError) {
        showError("Login Failed", error.message || 'An error occurred during login');
      }
    } finally {
      setLoading(false);
      setDisabled(false);
    }
  };

  const handleSuccessfulLogin = (responseData) => {
    const { user } = responseData;
    
    // Store user data in localStorage
    const userData = {
      'codefussion-login-token': user.email,
      'codefussion-student-first-name': user.first_name,
      'codefussion-student-last-name': user.last_name,
      'codefussion-student-email': user.email,
      'codefussion-student-photo': user.profile_photo,
      'codefussion-date-logged-in': user.date_loggedin,
      'codefussion-time-logged-in': user.time_loggedin,
      'codefussion-user-id': user.id
    };

    Object.entries(userData).forEach(([key, value]) => {
      localStorage.setItem(key, value || '');
    });

    // Show success message and redirect
    let timerInterval;
    Swal.fire({
      title: 'Login Successful',
      icon: "success",
      html: 'Redirecting in <b></b> milliseconds.',
      timer: 1000,
      timerProgressBar: true,
      didOpen: () => {
        Swal.showLoading();
        const timer = Swal.getHtmlContainer().querySelector('b');
        timerInterval = setInterval(() => {
          timer.textContent = Swal.getTimerLeft();
        }, 100);
      },
      willClose: () => {
        clearInterval(timerInterval);
      }
    }).then((result) => {
      if (result.dismiss === Swal.DismissReason.timer) {
        window.location.replace("/check-subscription");
      }
    });
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
      </div>
      
      <section className="login-section">
        <div className="login-card">
          <div className="login-header">
            <Link to="/">
              <img src={logo} className="logo" alt="Codeflare Logo" />
            </Link>
            <Link to="/" className="close-button">
              <i className="fas fa-times"></i>
            </Link>
          </div>
          
          <div className="login-content">
            <div className="login-form">
              <h2>Welcome Back</h2>
              <p className="login-subtitle text-center">Sign in to continue your learning journey</p>
              
              {/* Display login error message */}
              {loginError && (
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {loginError}
                </div>
              )}
              
              <form onSubmit={handleLogin}>
                <div className="input-group">
                  <div className="input-field">
                    <i className="fas fa-envelope input-icon"></i>
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required 
                      placeholder="Enter your email"
                      disabled={disabled}
                      className={loginError ? 'error-border' : ''}
                    />
                  </div>
                </div>
                
                <div className="input-group">
                  <div className="input-field">
                    <i className="fas fa-lock input-icon"></i>
                    <input 
                      type={passwordVisible ? "text" : "password"} 
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required 
                      placeholder="Enter your password"
                      disabled={disabled}
                      className={loginError ? 'error-border' : ''}
                    />
                    <i 
                      onClick={togglePasswordVisibility} 
                      className={`fa ${passwordVisible ? "fa-eye-slash" : "fa-eye"} password-toggle`}
                      style={{cursor: 'pointer'}}
                    ></i>
                  </div>
                </div>
                
                <div className="forgot-password">
                  <Link to="/forgot-password">Forgot Password?</Link>
                </div>
                
                <button 
                  type="submit"
                  disabled={disabled} 
                  className="login-button"
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" variant="light" size="sm" className="me-2" />
                      Signing In...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </button>
                
                <div className="register-link">
                  <p>Don't have an account? <Link to="/create-account">Create Account</Link></p>
                </div>
              </form>
            </div>
          </div>
          
          <div className="card-footer">
            <div className="footer-content">
              <span>&copy; {new Date().getFullYear()} Codeflare</span>
              <div className="footer-links">
                <a target="_blank" href="https://codeflarelimited.com/privacy-policy" rel="noopener noreferrer">Privacy</a>
                <a target="_blank" href="https://codeflarelimited.com/terms" rel="noopener noreferrer">Terms</a>
              </div>
            </div>
          </div>
        </div>
        
        <br />
        <div className="support-link">
          <p>
            Need help? <a href="https://codeflarelimited.com/contact" target="_blank" rel="noopener noreferrer">Contact support</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Login;